import React, { memo } from 'react';
import {
    Box,
    Typography,
    Alert,
    Button,
    Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTeacherDashboard } from '../../hooks/useDashboard';
import { useSubscription } from '../../context/SubscriptionContext';
import {
    MyAttendanceRecordCard,
    LeaveBalanceCard,
    TodayScheduleCard,
    PendingMarksEntryCard,
    DashboardRefreshButton,
} from '../../components/dashboard';

/**
 * Teacher Dashboard Page
 * Elegant, professional dashboard for Teachers
 * Clean layout matching School Admin dashboard aesthetics
 */
const TeacherDashboard: React.FC = memo(() => {
    const { data, loading, error, refetch } = useTeacherDashboard();
    const { hasFeature } = useSubscription();

    // Feature flags for conditional card rendering
    const showAttendanceCards = hasFeature('attendance');
    const showLeavesCards = hasFeature('leaves');
    const showTimetableCards = hasFeature('timetable');
    const showExaminationCards = hasFeature('examination');

    // Error state
    if (error && !loading) {
        return (
            <Box>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={refetch}>
                            Retry
                        </Button>
                    }
                    sx={{ borderRadius: 2 }}
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    // Loading state with skeleton
    if (loading) {
        return (
            <Box>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Skeleton variant="text" width={150} height={40} />
                        <Skeleton variant="text" width={280} height={20} />
                    </Box>
                </Box>

                {/* Skeleton Grid */}
                {(showAttendanceCards || showLeavesCards) && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {showAttendanceCards && (
                            <Grid size={{ xs: 12, sm: 6, lg: showLeavesCards ? 5 : 12 }}>
                                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                            </Grid>
                        )}
                        {showLeavesCards && (
                            <Grid size={{ xs: 12, sm: 6, lg: showAttendanceCards ? 7 : 12 }}>
                                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                            </Grid>
                        )}
                    </Grid>
                )}

                {(showTimetableCards || showExaminationCards) && (
                    <Grid container spacing={2}>
                        {showTimetableCards && (
                            <Grid size={{ xs: 12, sm: 6, lg: showExaminationCards ? 7 : 12 }}>
                                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                            </Grid>
                        )}
                        {showExaminationCards && (
                            <Grid size={{ xs: 12, sm: 6, lg: showTimetableCards ? 5 : 12 }}>
                                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Box>
        );
    }

    return (
        <Box>
            {/* Header - Clean, minimal like School Admin */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography 
                        variant="h4" 
                        fontWeight={600} 
                        color="text.primary"
                        gutterBottom
                    >
                        Dashboard
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                    >
                        Welcome back! Here's your overview for today.
                    </Typography>
                </Box>
                <DashboardRefreshButton onClick={refetch} />
            </Box>

            {/* Dashboard Grid - Matching School Admin spacing */}
            {/* Row 1: Attendance & Leave Balance - only show if features are enabled */}
            {(showAttendanceCards || showLeavesCards) && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {showAttendanceCards && (
                        <Grid size={{ xs: 12, sm: 6, lg: showLeavesCards ? 5 : 12 }}>
                            <MyAttendanceRecordCard
                                data={data?.attendanceSummary}
                                currentMonth={data?.currentMonth}
                                loading={loading}
                            />
                        </Grid>
                    )}
                    {showLeavesCards && (
                        <Grid size={{ xs: 12, sm: 6, lg: showAttendanceCards ? 7 : 12 }}>
                            <LeaveBalanceCard
                                data={data?.leaveBalance}
                                loading={loading}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Row 2: Today's Schedule & Pending Marks - only show if features are enabled */}
            {(showTimetableCards || showExaminationCards) && (
                <Grid container spacing={2}>
                    {showTimetableCards && (
                        <Grid size={{ xs: 12, sm: 6, lg: showExaminationCards ? 7 : 12 }}>
                            <TodayScheduleCard
                                data={data?.todaySchedule}
                                loading={loading}
                            />
                        </Grid>
                    )}
                    {showExaminationCards && (
                        <Grid size={{ xs: 12, sm: 6, lg: showTimetableCards ? 5 : 12 }}>
                            <PendingMarksEntryCard
                                initialPage={data?.pendingMarksEntry}
                                loading={loading}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
});

TeacherDashboard.displayName = 'TeacherDashboard';

export default TeacherDashboard;
